import { MdRefresh } from "react-icons/md";

import "../history.css";
import {
  EntryDate,
  HistoryFoot,
  HistoryToolbar,
  ProductChips,
  TableState,
  Text,
  groupRows,
  useHistoryQuery,
} from "./historyCommon";

/* ------------------------------------------------------------------ *
 * Work order history (redesign) — /work-history
 *
 * Replaces src/pages/workHistory.js (kept at /work-history-v1):
 *   - a 1200px-min bootstrap row strip with percentage-width <span>
 *     columns that added up to 120%          -> a real table that
 *                                               scrolls horizontally
 *                                               instead of overflowing
 *   - products run together as
 *     "2 x Casket cover, 1 x Wreath" in one
 *     30%-wide cell                          -> one chip per line item
 *   - the funeral / delivery date the page
 *     let you filter on was never a column   -> shown beside the entry
 *                                               date
 *   - the FD code, which is how a funeral
 *     order is actually referred to, was in
 *     the markup as a commented-out span     -> under the late person's
 *                                               name
 *   - `eval(listStart + num + 1)` for the row
 *     number                                 -> arithmetic
 *
 * Query, pager, chips and the fixes to the legacy fetch live in
 * ./historyCommon.js, shared with /customer-history.
 * ------------------------------------------------------------------ */

const safeCode = (raw) => {
  if (!raw) return "";
  if (typeof raw === "object") return raw.code || "";
  try {
    return (JSON.parse(raw) || {}).code || "";
  } catch (e) {
    return typeof raw === "string" ? raw : "";
  }
};

const COLUMNS = {
  Funeral: [
    { key: "num", label: "#", className: "hx-num" },
    { key: "lead", label: "Late person" },
    { key: "orderer", label: "Ordered by" },
    { key: "opdate", label: "Funeral date" },
    { key: "entry", label: "Entered" },
    { key: "cover", label: "Casket cover" },
    { key: "tributes", label: "Tributes" },
  ],
  Retail: [
    { key: "num", label: "#", className: "hx-num" },
    { key: "lead", label: "Recipient" },
    { key: "orderer", label: "Ordered by" },
    { key: "opdate", label: "Delivery date" },
    { key: "entry", label: "Entered" },
    { key: "products", label: "Products" },
  ],
};

function FuneralRow({ order, num }) {
  const code = safeCode(order.fd_code);
  return (
    <tr className="hx-row">
      <td className="hx-num">{num}</td>
      <td className="hx-lead">
        <span className="hx-lead-name">
          <Text value={order.late_person_name} empty="Unnamed" />
        </span>
        {code && <span className="hx-lead-sub">{code}</span>}
      </td>
      <td className="hx-plain">
        <Text value={order.ordered_by} />
        {order.orderer_phone && (
          <span className="hx-lead-sub">{order.orderer_phone}</span>
        )}
      </td>
      <td className="hx-date-v">
        <Text value={order.funeral_date} />
        {order.funeral_time && <span>{order.funeral_time}</span>}
      </td>
      <td>
        <EntryDate value={order.date || order.timestamp} />
      </td>
      <td className="hx-prods">
        <ProductChips groups={[order.casket_cover_selected]} empty="None" />
      </td>
      <td className="hx-prods">
        <ProductChips groups={[order.tributes_selected]} empty="None" />
      </td>
    </tr>
  );
}

function RetailRow({ order, num }) {
  return (
    <tr className="hx-row">
      <td className="hx-num">{num}</td>
      <td className="hx-lead">
        <span className="hx-lead-name">
          <Text value={order.recipient_name} empty="Unnamed" />
        </span>
        {order.company_name && (
          <span className="hx-lead-sub">{order.company_name}</span>
        )}
      </td>
      <td className="hx-plain">
        <Text value={order.ordered_by} />
        {order.orderer_phone && (
          <span className="hx-lead-sub">{order.orderer_phone}</span>
        )}
      </td>
      <td className="hx-date-v">
        <Text value={order.delivery_date} />
        {order.delivery_time && <span>{order.delivery_time}</span>}
      </td>
      <td>
        <EntryDate value={order.date || order.timestamp} />
      </td>
      <td className="hx-prods">
        <ProductChips groups={[order.products_selected]} empty="None" />
      </td>
    </tr>
  );
}

export function WorkHistoryRedesign() {
  const q = useHistoryQuery("Funeral");
  const columns = COLUMNS[q.orderType];
  const Row = q.orderType === "Funeral" ? FuneralRow : RetailRow;
  const rows = groupRows(q.orders);

  const dateWord = q.orderType === "Funeral" ? "funeral" : "delivery";

  return (
    <div className="hx-page">
      <HistoryToolbar
        q={q}
        searchPlaceholder={
          q.orderType === "Funeral"
            ? "Search by late person, orderer or FD code"
            : "Search by recipient, company or orderer"
        }
      >
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={q.refresh}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
      </HistoryToolbar>

      <div className="hx-body">
        <p className="hx-lede">
          Every <b>{q.orderType.toLowerCase()}</b> order ever entered, newest
          first.{" "}
          {q.date ? (
            <>
              Filtered to a {dateWord} date of <b>{q.date}</b>.
            </>
          ) : (
            <>All {dateWord} dates.</>
          )}
          {q.query && (
            <>
              {" "}
              Matching <b>“{q.query}”</b>.
            </>
          )}
        </p>

        <div className="hx-tablewrap">
          <div className="hx-scroll">
            <table className="hx-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className={c.className}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <TableState
                  q={q}
                  cols={columns.length}
                  emptyLead={
                    q.query || q.date
                      ? "No order matches these filters."
                      : `No ${q.orderType.toLowerCase()} orders yet.`
                  }
                  emptyHint={
                    q.query || q.date
                      ? "Clear the date or the search to widen the list."
                      : "Orders appear here as soon as they are entered."
                  }
                />
                {!q.loading &&
                  !q.loadError &&
                  rows.map((row) =>
                    row.group ? (
                      <tr className="hx-grouprow" key={"g" + row.group}>
                        <td colSpan={columns.length}>
                          <div className="hx-group">
                            <span className="hx-group-d">{row.label}</span>
                            <span className="hx-group-n">
                              {row.count} {row.count === 1 ? "order" : "orders"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <Row
                        key={row.order._id || row.index}
                        order={row.order}
                        num={q.listStart + row.index + 1}
                      />
                    )
                  )}
              </tbody>
            </table>
          </div>
          <HistoryFoot q={q} />
        </div>
      </div>
    </div>
  );
}

export default WorkHistoryRedesign;
